import { shallowEqual } from 'tangle-shared'
import { State } from 'interfaces/state'
import { StoreDispatch } from 'interfaces/component'
import { createOwnPropsSelector } from 'selectors/globalSelectors'
import { Guard, Guards, GuardsParam, SimpleGuard, WatchingGuard } from 'interfaces/guards'
import { Fetchable, LoadedFetchable, LoadingFetchable, ErroredFetchable } from 'interfaces/fetchable'
import {
  ComponentDecorator,
  connect,
  connectAdvanced,
  MapDispatchToPropsFunction,
  MapDispatchToPropsParam,
  MapStateToPropsParam,
  SelectorFactory,
  FuncOrSelf,
} from 'react-redux'
import { bindActionCreators } from 'redux'
import { createSelector } from 'reselect'
import * as isEqual from 'lodash/isEqual'
import ShadedLoadable from 'components/ShadedLoadable'
import Loadable, { LoadableOwnProps, LoadableProps } from 'components/Loadable'
import DefaultPreloader from 'components/atoms/preloaders/DefaultPreloader'
import StaleableLoadable, {
  StaleableLoadableOwnProps,
  StaleableLoadableProps,
} from 'components/features/pfm/StaleableLoadable/StaleableLoadableContainer'
import { GUARD_LOADING_TIMEOUT } from 'constants/component'
import { captureTimedOutComponent } from 'helpers/errorHelpers'
import { useState, useEffect, useRef, useMemo } from 'react'
import { ComponentType } from 'react'
import { useStoreSelector, useStoreDispatch } from 'helpers/reactReduxHelpers'
import debounce from 'lodash/debounce'
import { useShallowMemo } from 'helpers/hookHelpers'
import { Action } from 'actions'

/*
  DEPRECATED
  Due to migration to newer version of TS broke a lot of typings of HOCs like `connectLoadable`
*/


// TODO rewrite guards to correspond next requirements:
// 1. No memoized state of parallel guards (use only global state somehow)
// 2. Pass to inner containers not-null data

/**
 * Please always provide generic explicitly
 */
export function connectLoadable<TStateProps, TDispatchProps = {}, TOwnProps = {}, TContainerOwnProps = {}> (
  guardsParam: GuardsParam<TOwnProps & TContainerOwnProps>,
  preloader: ComponentType<{}> | false = DefaultPreloader
) {
  return function (
    mapStateToProps?: MapStateToPropsParam<TStateProps, TOwnProps & TContainerOwnProps>,
    mapDispatchToProps?: MapDispatchToPropsParam<TDispatchProps, TOwnProps & TContainerOwnProps>,
  ): ComponentDecorator<TStateProps & TDispatchProps & TOwnProps, TOwnProps & TContainerOwnProps> {
    return (content) => {
      const contentContainer = connect(mapStateToProps, mapDispatchToProps)(content as any)
      return connectLoadableContainer(guardsParam, contentContainer, preloader) as any
    }
  }
}

function connectLoadableContainer<TOwnProps extends {}> (
  guardsParam: GuardsParam<TOwnProps>,
  content: ComponentType<TOwnProps>,
  preloader: ComponentType<{}> | false = DefaultPreloader
) {
  const selectorFactory = createLoadableSelectorFactory(guardsParam, content, preloader)
  const factory = connectAdvanced(selectorFactory)

  return factory(Loadable as any)
}


function createLoadableSelectorFactory<TOwnProps> (
  guardsParam: GuardsParam<TOwnProps>,
  content: ComponentType<TOwnProps>,
  preloader: ComponentType<{}> | false = DefaultPreloader
): SelectorFactory<State, LoadableProps<TOwnProps>, LoadableOwnProps<TOwnProps>> {
  return (dispatch) => {
    // this function is called when instance of component is mount,
    // so we can initialize unique guards instance here
    const guards = typeof guardsParam === 'function'
      ? guardsParam()
      : guardsParam

    const guardsChecker = createGuardsChecker(guards)
    const guardsWatcher = createGuardsWatcher(guards)
    const guardsLoader = createGuardsLoader(guards)

    let flags: boolean[]
    let watches: {}[]
    let loaders: (() => void)[]
    let ownProps: TOwnProps | undefined
    let props: LoadableProps<TOwnProps>

    return (nextState, nextOwnProps) => {
      let isDirty = false
      if (!shallowEqual(ownProps, nextOwnProps)) {
        isDirty = true
        ownProps = nextOwnProps
      }

      const nextFlags = guardsChecker(nextState, nextOwnProps)
      if (!isEqual(nextFlags, flags)) {
        isDirty = true
        flags = nextFlags
      }

      const nextWatches = guardsWatcher(nextState, nextOwnProps)
      if (!isEqual(nextWatches, watches)) {
        isDirty = true
        watches = nextWatches
        loaders = guardsLoader(dispatch, watches)
      }

      // no shallow compare here
      if (isDirty) {
        props = Object.assign(
          {},
          nextOwnProps,
          {
            flags,
            loaders,
            content,
            preloader,
            loaderNames: guards.map((guard) => guard.name),
          }
        ) as LoadableProps<TOwnProps>
      }

      return props
    }
  }
}


function createGuardsChecker<TOwnProps> (guards: Guards<TOwnProps>) {
  const triggers = guards.map((guard) => guard.checker)

  // Here used capability to pass selectors as array:
  // https://github.com/reactjs/reselect#createselectorinputselectors--inputselectors-resultfunc
  // but current typings of reselect doesn't cover that case,
  // so we are forced to use any
  return createSelector(
    triggers as any,
    (...data: boolean[]) => data
  ) as (state: State, ownProps?: TOwnProps) => boolean[]
}

function createGuardsWatcher<TOwnProps> (guards: Guards<TOwnProps>) {
  const ownPropsSelector = createOwnPropsSelector()
  const watchers = guards.map((guard) => isWatchingGuard(guard) ? guard.watcher : ownPropsSelector)

  // Here used capability to pass selectors as array:
  // https://github.com/reactjs/reselect#createselectorinputselectors--inputselectors-resultfunc
  // but current typings of reselect doesn't cover that case,
  // so we are forced to use any
  return createSelector(
    watchers as any,
    (...data: any[]) => data
  ) as (state: State, ownProps?: TOwnProps) => any[]
}

function createGuardsLoader<TOwnProps> (guards: Guards<TOwnProps>) {
  return (dispatch: StoreDispatch, watches: any[]): (() => void)[] =>
    guards.map((guard, idx) => isWatchingGuard(guard) ?
      () => guard.loader(dispatch, watches[idx]) :
      () => guard.loader(dispatch, watches[idx] as TOwnProps))
}


// ===========================================================================//
//                                OTHER UTILS                                 //
// ===========================================================================//


function isWatchingGuard<T> (guard: Guard<T>): guard is WatchingGuard<T, any> {
  const field: keyof WatchingGuard<T, any> = 'watcher'
  return guard.hasOwnProperty(field)
}

// ===========================================================================//
//                               PARALLEL GUARD                               //
// ===========================================================================//


/**
 * Just combine guards into one
 */
function combineGuards<TOwnProps> (...guards: Guard<TOwnProps>[]): WatchingGuard<TOwnProps, any> {
  const ownPropsSelector = createOwnPropsSelector()

  return {
    get name () {
      return `Combined(${ guards.map((guard) => guard.name).join(', ') })`
    },
    checker: (state, ownProps) => {
      // cannot just return .every result
      // because each memoized guard should be recalculated
      const childrenFlags = guards.map((guard) => guard.checker(state, ownProps))
      return childrenFlags.every(Boolean)
    },
    watcher: (state, ownProps) =>
      guards.map((guard) => isWatchingGuard(guard)
        ? guard.watcher(state, ownProps)
        : ownPropsSelector(state, ownProps)),
    loader: (dispatch: StoreDispatch, watch: any[]) =>
      guards.forEach((guard, idx) =>
        isWatchingGuard(guard)
          ? guard.loader(dispatch, watch[idx])
          : guard.loader(dispatch, watch[idx] as TOwnProps)),
  }
}

/**
 * Create memoized guard from one
 * Memoized guard won't call loader if last calling of checker returns true
 */
function createMemoizedGuard<TOwnProps> (guard: Guard<TOwnProps>): Guard<TOwnProps> {
  let isLoaded = false

  if (!isWatchingGuard(guard)) {
    return {
      get name () {
        return isLoaded
          ? `Passed(${ guard.name })`
          : guard.name
      },
      checker: (state, ownProps) => {
        isLoaded = guard.checker(state, ownProps)
        return isLoaded
      },
      loader: (dispatch: StoreDispatch, ownProps: TOwnProps) => {
        if (!isLoaded) {
          guard.loader(dispatch, ownProps)
        }
      },
    }
  }

  return {
    get name () {
      return isLoaded
        ? `Passed(${ guard.name })`
        : guard.name
    },
    checker: (state, ownProps) => {
      isLoaded = guard.checker(state, ownProps)
      return isLoaded
    },
    watcher: guard.watcher,
    loader: (dispatch: StoreDispatch, watch: typeof guard.watcher) => {
      if (!isLoaded) {
        guard.loader(dispatch, watch)
      }
    },
  }
}

/**
 * Create smart guard that won't call loaders of loaded guard
 *
 * Technically: memoize all selectors and combine them it into one
 */
export function createParallelGuard<T1, T2> (g1: Guard<T1>, g2: Guard<T2>): Guard<T1 & T2>
export function createParallelGuard<T1, T2, T3> (g1: Guard<T1>, g2: Guard<T2>, g3: Guard<T3>): Guard<T1 & T2 & T3>
export function createParallelGuard<T1, T2, T3, T4> (g1: Guard<T1>, g2: Guard<T2>, g3: Guard<T3>, g4: Guard<T4>): Guard<T1 & T2 & T3 & T4>
export function createParallelGuard<T1, T2, T3, T4, T5> (g1: Guard<T1>, g2: Guard<T2>, g3: Guard<T3>, g4: Guard<T4>, g5: Guard<T5>): Guard<T1 & T2 & T3 & T4 & T5>
export function createParallelGuard<TOwnProps> (...guards: SimpleGuard<TOwnProps>[]): SimpleGuard<TOwnProps> {
  const memoizedGuards = guards.map(createMemoizedGuard)
  return combineGuards(...memoizedGuards)
}

// There is no possibility to bind watch
export function bindGuard<TOwnProps, TWatch> (guard: WatchingGuard<TOwnProps, TWatch>, boundProps: TOwnProps): WatchingGuard<{}, TWatch>
export function bindGuard<TOwnProps, TResultKey extends keyof TOwnProps, TWatch> (
  guard: WatchingGuard<TOwnProps, TWatch>,
  boundProps: Partial<TOwnProps>
): WatchingGuard<Pick<TOwnProps, TResultKey>, TWatch>

export function bindGuard<TOwnProps> (guard: SimpleGuard<TOwnProps>, boundProps: TOwnProps): SimpleGuard<{}>
export function bindGuard<TOwnProps, TResultKey extends keyof TOwnProps> (
  guard: SimpleGuard<TOwnProps>,
  boundProps: Partial<TOwnProps>
): SimpleGuard<Pick<TOwnProps, TResultKey>>

export function bindGuard<TOwnProps, TResultKey extends keyof TOwnProps> (
  guard: Guard<TOwnProps>,
  boundProps: Partial<TOwnProps>
): Guard<Pick<TOwnProps, TResultKey>> {
  const bindProps = (ownProps: Pick<TOwnProps, TResultKey>): TOwnProps =>
    Object.assign({}, ownProps, boundProps as TOwnProps)

  const checker = (state: State, ownProps: Pick<TOwnProps, TResultKey>) =>
    guard.checker(state, bindProps(ownProps))

  if (isWatchingGuard(guard)) {
    return {
      name: `Bound(${ guard.name })`,
      checker,
      watcher: (state: State, ownProps: Pick<TOwnProps, TResultKey>) =>
        guard.watcher(state, bindProps(ownProps)),
      loader: guard.loader,
    }
  }

  return {
    name: `Bound(${ guard.name })`,
    checker,
    loader: (dispatch: StoreDispatch, ownProps: Pick<TOwnProps, TResultKey>) =>
      guard.loader(dispatch, bindProps(ownProps)),
  }
}


// =========================================================================== //
//                               CONNECT SHADED                                //
// =========================================================================== //

function isSelf<T> (thing: FuncOrSelf<T>): thing is T {
  return typeof thing !== 'function' ||
    thing.length > 0
}

function makeSelf<T> (thing: FuncOrSelf<T>): T {
  if (isSelf(thing)) {
    return thing
  }

  return thing()
}

function isMapDispatchToPropsFunction<TDispatchProps, TOwnProps> (thing: MapDispatchToPropsFunction<TDispatchProps, TOwnProps> | TDispatchProps):
  thing is MapDispatchToPropsFunction<TDispatchProps, TOwnProps> {
  return typeof thing === 'function'
}

function makeDispatchToPropsFunction<TDispatchProps, TOwnProps> (
  thing: MapDispatchToPropsFunction<TDispatchProps, TOwnProps> | TDispatchProps
): MapDispatchToPropsFunction<TDispatchProps, TOwnProps> {
  if (isMapDispatchToPropsFunction(thing)) {
    return thing
  }

  return (dispatch: StoreDispatch) =>
    // reason of any is inconsistence of redux and react-redux typings
    bindActionCreators(thing as any, dispatch)
}

// returns only stateProps & dispatchProps
function createComponentSelectorFactory <TStateProps, TDispatchProps, TOwnProps> (
  createMapStateToProps: MapStateToPropsParam<TStateProps, TOwnProps>,
  createMapDispatchToProps?: MapDispatchToPropsParam<TDispatchProps, TOwnProps>
): SelectorFactory<State, TStateProps, TOwnProps> {
  return (dispatch) => {
    const mapStateToProps = makeSelf(createMapStateToProps)
    const mapDispatchToProps = createMapDispatchToProps
      ? makeDispatchToPropsFunction(makeSelf(createMapDispatchToProps))
      : undefined

    let lastStateProps: TStateProps | null = null
    let lastDispatchProps: TDispatchProps | null = null
    let lastOwnProps: TOwnProps | undefined
    let lastResultProps: TStateProps & TDispatchProps | null = null

    return (nextState, nextOwnProps) => {
      let isDirty = false

      const nextProps = mapStateToProps(nextState, nextOwnProps as TOwnProps)
      if (!shallowEqual(lastStateProps, nextProps)) {
        lastStateProps = nextProps
        isDirty = true
      }

      if (mapDispatchToProps) {
        if (!shallowEqual(lastOwnProps, nextOwnProps)) {
          isDirty = true
          lastOwnProps = nextOwnProps
          lastDispatchProps = mapDispatchToProps(dispatch, nextOwnProps as TOwnProps)
        }
      }

      if (isDirty) {
        lastResultProps = Object.assign({}, lastStateProps, lastDispatchProps)
      }

      return lastResultProps as TStateProps & TDispatchProps
    }
  }
}

type ResultSelectorFactory<TStateProps, TOwnProps> = SelectorFactory<State, TStateProps & LoadableProps<TOwnProps>, LoadableOwnProps<TOwnProps>>
function createResultSelectorFactory <TStateProps, TOwnProps> (
  cmpFactory: SelectorFactory<State, TStateProps, TOwnProps>,
  loadFactory: SelectorFactory<State, LoadableProps<TOwnProps>, LoadableOwnProps<TOwnProps>>
): ResultSelectorFactory<TStateProps, TOwnProps> {
  return (dispatch) => {
    const cmpSelector = cmpFactory(dispatch, {})
    const loadSelector = loadFactory(dispatch, {})

    let lastLoadProps: LoadableProps<TOwnProps> | null = null
    let lastComponentProps: TStateProps | null = null
    let lastResult: TStateProps & LoadableProps<TOwnProps> | null = null

    return (nextState, nextOwnProps) => {
      let isDirty = false
      const nextLoadProps = loadSelector(nextState, nextOwnProps)

      if (lastLoadProps !== nextLoadProps) {
        isDirty = true
        lastLoadProps = nextLoadProps
      }

      const isLoaded = nextLoadProps.flags.every(Boolean)
      if (isLoaded) {
        const nextComponentProps = cmpSelector(nextState, nextOwnProps)
        if (lastComponentProps !== nextComponentProps) {
          isDirty = true
          lastComponentProps = nextComponentProps
        }
      } else {
        lastComponentProps = null
      }

      if (isDirty) {
        lastResult = Object.assign({}, lastComponentProps, lastLoadProps)
      }

      return lastResult as TStateProps & LoadableProps<TOwnProps>
    }
  }
}

export function connectStaleableLoadable<TStateProps, TDispatchProps = {}, TOwnProps = {}, TContainerOwnProps = {}> (
  guardsParam: GuardsParam<TOwnProps & TContainerOwnProps>,
  isDataPresentSelector: (state: State, ownProps: TOwnProps) => boolean,
  preloader: ComponentType<{}> = DefaultPreloader
) {
  return function (
    mapStateToProps?: MapStateToPropsParam<TStateProps, TOwnProps & TContainerOwnProps>,
    mapDispatchToProps?: MapDispatchToPropsParam<TDispatchProps, TOwnProps & TContainerOwnProps>,
  ): ComponentDecorator<TStateProps & TDispatchProps & TOwnProps, TOwnProps & TContainerOwnProps> {
    return (content: any) => {
      const contentContainer = connect(mapStateToProps, mapDispatchToProps)(content) as any
      return connectStaleableLoadableContainer(guardsParam, isDataPresentSelector, contentContainer, preloader) as any
    }
  }
}

export function connectStaleableLoadableContainer<TOwnProps extends {}, TContainerOwnProps> (
  guardsParam: GuardsParam<TOwnProps>,
  isDataPresentSelector: (state: State, ownProps: TOwnProps) => boolean,
  content: ComponentType<TOwnProps>,
  preloader: ComponentType<{}>
) {
  const selectorFactory = createStaleableLoadableSelectorFactory(guardsParam, isDataPresentSelector, content, preloader)
  const LoadableComponent = StaleableLoadable as new () => StaleableLoadable<TOwnProps & TContainerOwnProps>

  return connectAdvanced(selectorFactory)(LoadableComponent as any)
}

function createStaleableLoadableSelectorFactory<TOwnProps> (
  guardsParam: GuardsParam<TOwnProps>,
  isDataPresentSelector: (state: State, ownProps: TOwnProps) => boolean,
  content: ComponentType<TOwnProps>,
  preloader: ComponentType<{}> | false = DefaultPreloader
): SelectorFactory<State, StaleableLoadableProps<TOwnProps>, StaleableLoadableOwnProps<TOwnProps>> {
  return (dispatch) => {
    // this function is called when instance of component is mount,
    // so we can initialize unique guards instance here
    const guards = typeof guardsParam === 'function'
      ? guardsParam()
      : guardsParam

    const guardsChecker = createGuardsChecker(guards)
    const guardsWatcher = createGuardsWatcher(guards)
    const guardsLoader = createGuardsLoader(guards)

    let hasData: boolean
    let flags: boolean[]
    let watches: {}[]
    let loaders: (() => void)[]
    let props: StaleableLoadableProps<TOwnProps>

    return (nextState, nextOwnProps) => {
      let isDirty = false

      const nextHasData = isDataPresentSelector(nextState, nextOwnProps)

      if (nextHasData !== hasData) {
        isDirty = true
        hasData = nextHasData
      }

      const nextFlags = guardsChecker(nextState, nextOwnProps)
      if (!isEqual(nextFlags, flags)) {
        isDirty = true
        flags = nextFlags
      }

      const nextWatches = guardsWatcher(nextState, nextOwnProps)
      if (!isEqual(nextWatches, watches)) {
        isDirty = true
        watches = nextWatches
        loaders = guardsLoader(dispatch, watches)
      }

      // no shallow compare here
      if (isDirty) {
        const isLoaded = flags.every(Boolean)

        props = Object.assign(
          {},
          nextOwnProps,
          {
            content,
            preloader,
            isLoaded,
            hasData,
            loaders,
            loaderNames: guards.map((guard) => guard.name),
            flags,
          }
        ) as StaleableLoadableProps<TOwnProps>
      }

      return props
    }
  }
}

export function connectShadedLoadable<TStateProps, TDispatchProps = {}, TOwnProps = {}, TContainerOwnProps = {}> (
  createMapStateToProps: MapStateToPropsParam<TStateProps, TOwnProps & TContainerOwnProps>,
  createMapDispatchToProps: undefined | MapDispatchToPropsParam<TDispatchProps, TOwnProps & TContainerOwnProps>,
  guardsParam: GuardsParam<TOwnProps & TContainerOwnProps>,
  preloader: ComponentType<{}> | false = DefaultPreloader
): ComponentDecorator<TStateProps & TDispatchProps & TOwnProps, TOwnProps & TContainerOwnProps> {
  return (content: any) => {
    const loadSelectorFactory = createLoadableSelectorFactory(guardsParam, content as any, preloader)
    const cmpSelectorFactory = createComponentSelectorFactory(createMapStateToProps, createMapDispatchToProps)
    const selectorFactory = createResultSelectorFactory(cmpSelectorFactory, loadSelectorFactory)

    return connectAdvanced(selectorFactory)(
      ShadedLoadable as any
    ) as any
  }
}

export function getFirstUnloadedGuardIndex (flags: boolean[]) {
  return flags.findIndex((isLoaded) => !isLoaded)
}

export function checkGuards (flags: boolean[], loaders: (() => void)[]) {
  const firstUnloaded = getFirstUnloadedGuardIndex(flags)

  if (firstUnloaded > -1) {
    /**
     * EXPERIMENT:
     *
     * This `setTimeout` thing is needed to allow the `distinct` cache drop.
     * For example, `GET_MEMBER_TREND` and `SET_BALANCE_ACCOUNT`:
     * A reducer state is dropped upon the `SET_BALANCE_ACCOUNT` action,
     * loadables start to re-render and generate `GET_MEMBER_TREND` action.
     *
     * But the thing is that `GET_MEMBER_TREND` come to an epic sooner than `SET_BALANCE_ACCOUNT`,
     * therefore `distinct` cache is dropped later than `GET_MEMBER_TREND` come.
     *
     * And this `setTimeout` solves the issue.
     */
    setTimeout(() => loaders[firstUnloaded](), 0)
  }
}

export function checkIfLoaded (isLoaded: boolean, flags: boolean[], loaderNames: string[], content: ComponentType) {
  if (!isLoaded) {
    const hangingGuardIndex = getFirstUnloadedGuardIndex(flags)
    const loaderName = loaderNames[hangingGuardIndex]
    captureTimedOutComponent(content.name, loaderName, GUARD_LOADING_TIMEOUT)
  }
}

// HOOKS

// it's better to rework guards with `isLoading` flag
// TODO strict props (now it's possible to pass empty own props to guard with non-empty own props)
type UseSimpleGuardOptions = {
  fixPropsCheck?: boolean
}
export function useSimpleGuard<TOwnProps extends {}> (guard: SimpleGuard<TOwnProps>, ownProps: TOwnProps, moduleName: string, options?: UseSimpleGuardOptions) {
  const memoizedOwnProps = useShallowMemo(ownProps)
  const isLoaded = useStoreSelector((state) => guard.checker(state, memoizedOwnProps))
  const [lastCalledParams, setLastCalledParams] = useState<TOwnProps | null>(null)
  const dispatch = useStoreDispatch()

  const cacheRef = useRef(Math.random())
  useMemo(() => {
    if (options?.fixPropsCheck && lastCalledParams !== memoizedOwnProps) {
      cacheRef.current = Math.random()
    }
  }, [lastCalledParams, memoizedOwnProps, options])


  useEffect(() => {
    if (!options?.fixPropsCheck && lastCalledParams === memoizedOwnProps) {
      return
    }

    if (isLoaded) {
      return
    }

    setLastCalledParams(memoizedOwnProps)
    guard.loader(dispatch, memoizedOwnProps)

    const captureTimeout = debounce(() => {
      captureTimedOutComponent(moduleName, guard.name, GUARD_LOADING_TIMEOUT)
    }, GUARD_LOADING_TIMEOUT)

    return () => {
      captureTimeout.cancel()
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, memoizedOwnProps, cacheRef.current])

  return isLoaded
}

export function isFetchableLoaded<TData> (fetchable: Fetchable<TData>): fetchable is LoadedFetchable<TData> {
  return Boolean(fetchable.data) && fetchable.errorCode === null && !fetchable.isLoading
}

export function isFetchableLoading<TData> (fetchable: Fetchable<TData>): fetchable is LoadingFetchable<TData> {
  return fetchable.isLoading
}

export function isFetchableErrored<TData> (fetchable: Fetchable<TData>): fetchable is ErroredFetchable<TData> {
  return fetchable.errorCode !== null
}

export function createFetchableReducer<TData> () {
  return (prevState: Fetchable<TData>, action: Action): Fetchable<TData> => {
    if (!('meta' in action)) {
      return prevState
    }

    if (action.meta === 'request' && !isFetchableLoading(prevState)) {
      return {
        ...prevState,
        isLoading: true,
      }
    }

    if (action.meta === 'fail') {
      let nextErrorCode = prevState.errorCode
      if (!isFetchableErrored(prevState) && 'errorCode' in action.payload) {
        nextErrorCode = action.payload.errorCode
      }

      const withErrorsMap = ('errorCode' in action.payload) ? { errorsMap: action.payload.errorsMap } : {}

      return {
        ...prevState,
        errorCode: nextErrorCode,
        isLoading: false,
        ...withErrorsMap,
      }
    }

    if (action.meta === 'success' && !isFetchableLoaded(prevState)) {
      return {
        errorCode: null,
        isLoading: false,
        data: action.payload.response as TData,
      }
    }

    return prevState
  }
}

export function createEmptyFetchable<TData> (): Fetchable<TData> {
  return {
    isLoading: false,
    errorCode: null,
    data: null,
  }
}
